import { CaretDownOutlined, CheckOutlined } from '@vindr/icons';
import { addToArray } from '@vindr/utils/helpers/common.helper';
import { Row, Select } from 'antd';
import isEqual from 'lodash/isEqual';
import { Suspense, cloneElement, memo } from 'react';
import { Updater, useImmer } from 'use-immer';
import ClearFilter from './templates/ClearFilter';
import SearchFilter from './templates/SearchFilter';
import cx from '@vindr/utils/helpers/cx';

const { Option } = Select;

export interface FilterList {
  label: string;
  hidden: boolean;
  key: string | string[];
  component: JSX.Element;
}

export interface FilterGroupProps {
  filterList: {
    [key: string]: FilterList;
  };
  showList: Array<string>;
  hiddenList: Array<string>;
  onSearch: () => void;
  onClear: () => void;
  setFilter: Updater<any>;
  initialFilter: Record<string, any>;
  actionChildren?: JSX.Element;
  wrapperClassName?: string;
}

export type useFilterItemsType = [
  FilterGroupProps['filterList'],
  FilterGroupProps['hiddenList'],
  FilterGroupProps['showList']
];

const FilterGroup = ({
  actionChildren,
  filterList,
  hiddenList,
  initialFilter,
  onClear,
  onSearch,
  setFilter,
  showList,
  wrapperClassName,
}: FilterGroupProps) => {
  const [searchFields, setSearchFields] = useImmer(showList);
  const [options, setOptions] = useImmer(hiddenList);

  const handleChangeSearchKey = (selected: string, newSelected: string) => {
    if (selected === newSelected) return;
    const index = searchFields.indexOf(selected);

    // Clean value of old key
    setFilter((draft) => {
      const updateDraft = (keysToDelete: string | string[], keysToAdd: string | string[]) => {
        const deleteKeys = addToArray(keysToDelete);
        deleteKeys.forEach((key) => delete draft[key]);

        const addKeys = addToArray(keysToAdd);
        addKeys.forEach((key) => (draft[key] = initialFilter?.[key]));
      };

      const selectedKey = filterList[selected].key;
      const newSelectedKey = filterList[newSelected].key;

      updateDraft(selectedKey, newSelectedKey);
    });

    setSearchFields((draft) => {
      draft[index] = newSelected;
    });

    setOptions((draft) => {
      draft.push(selected);
      const index = draft.indexOf(newSelected);
      draft.splice(index, 1);
    });
  };

  return (
    <Row align="bottom" className={cx('ml-[-7px] space-y-2 pt-3 pb-6', wrapperClassName)}>
      {searchFields &&
        filterList &&
        searchFields.map((key: string) => {
          const field = filterList[key];
          const Component = field.component;

          return (
            <div key={key} className="search-item">
              <Row className="mr-1">
                {options.length ? (
                  <Select
                    bordered={false}
                    dropdownMatchSelectWidth={false}
                    menuItemSelectedIcon={<CheckOutlined />}
                    size="small"
                    suffixIcon={<CaretDownOutlined />}
                    value={field.label}
                    onChange={(value) => handleChangeSearchKey(key, value)}
                  >
                    {options.map((key: string | number) => {
                      const field = filterList[key];
                      return (
                        <Option key={field.label} value={key}>
                          {field.label}
                        </Option>
                      );
                    })}
                  </Select>
                ) : (
                  <div className="filter-title pb-1 pt-[6px] pl-[7px]">{field.label}</div>
                )}
              </Row>
              <Row className="pl-[7px]">
                <Suspense fallback={<div className="w-32" />}>{Component}</Suspense>
              </Row>
            </div>
          );
        })}

      <div className="ml-[7px] flex w-full flex-[1_0_auto] flex-wrap min-[640px]:w-[unset]">
        <SearchFilter onClick={onSearch} />
        <ClearFilter className="ml-2" onClick={onClear} />
        {actionChildren &&
          cloneElement(actionChildren, {
            className: cx(
              'mt-2 min-[640px]:ml-auto min-[640px]:mt-0 flex',
              actionChildren?.props?.className
            ),
          })}
      </div>
    </Row>
  );
};

export const GenerateListFilter = (
  filterList: FilterGroupProps['filterList']
): [FilterGroupProps['hiddenList'], FilterGroupProps['showList']] => {
  const hiddenList = [];
  const showList = [];

  Object.entries(filterList).forEach(([key, value]) => {
    if (value.hidden) hiddenList.push(key);
    else showList.push(key);
  });

  return [hiddenList, showList];
};

export default memo(FilterGroup, (prevProps, nextProps) => {
  return isEqual(prevProps.filterList, nextProps.filterList);
});
